import type { InstrumentSlotPayload } from "../lib/instrumentClipboard";
import { cloneInstrumentSlotPayload } from "../lib/instrumentClipboard";
import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";
import type { RemovedTrackPayload } from "./projectBridge";

function requireProject(context: AdamCommandContext) {
  const project = context.project;
  if (!project) {
    throw new Error("Adam project bridge is not registered");
  }
  return project;
}

/** Append one empty instrument track. */
export class AddTrackCommand implements AdamCommand {
  readonly id = "addTrack";
  readonly label = "Add track";
  private slot: number | undefined;
  private undone: RemovedTrackPayload | undefined;

  execute(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (this.undone) {
      project.restoreRemovedTrack(this.undone);
      this.slot = this.undone.track;
      this.undone = undefined;
      return { changed: "tracks" };
    }
    const added = project.addTrack();
    if (!added) {
      throw new Error("Cannot add track");
    }
    this.slot = added.slot;
    return { changed: "tracks" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (this.slot === undefined) {
      throw new Error("AddTrackCommand has no slot to undo");
    }
    this.undone = project.removeTrack(this.slot) ?? undefined;
    return { changed: "tracks" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Delete one instrument track (keeps ≥1). */
export class RemoveTrackCommand implements AdamCommand {
  readonly id = "removeTrack";
  readonly label = "Delete track";
  private payload: RemovedTrackPayload | undefined;

  constructor(readonly track: number) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (!this.payload) {
      this.payload = project.removeTrack(this.track) ?? undefined;
      if (!this.payload) {
        throw new Error("Cannot delete track");
      }
    } else {
      // Redo after undo: track is restored at payload.track.
      const again = project.removeTrack(this.payload.track);
      if (!again) {
        throw new Error("Cannot redo delete track");
      }
      this.payload = again;
    }
    return { changed: "tracks" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (!this.payload) {
      throw new Error("RemoveTrackCommand has no payload");
    }
    project.restoreRemovedTrack(this.payload);
    return { changed: "tracks" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Clone a track and insert the copy immediately to its right. */
export class DuplicateTrackCommand implements AdamCommand {
  readonly id = "duplicateTrack";
  readonly label = "Duplicate track";
  private createdSlot: number | undefined;
  private removed: RemovedTrackPayload | undefined;

  constructor(readonly track: number) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (this.removed) {
      project.restoreRemovedTrack(this.removed);
      this.createdSlot = this.removed.track;
      this.removed = undefined;
      return { changed: "tracks" };
    }
    const added = project.duplicateTrack(this.track);
    if (!added) {
      throw new Error("Cannot duplicate track");
    }
    this.createdSlot = added.slot;
    return { changed: "tracks" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (this.createdSlot === undefined) {
      throw new Error("DuplicateTrackCommand has no slot to undo");
    }
    this.removed = project.removeTrack(this.createdSlot) ?? undefined;
    return { changed: "tracks" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Move one instrument track to a new index (undo = reverse move). */
export class ReorderTracksCommand implements AdamCommand {
  readonly id = "reorderTracks";
  readonly label = "Reorder tracks";

  constructor(
    readonly fromIndex: number,
    readonly toIndex: number,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (!project.reorderTracks(this.fromIndex, this.toIndex)) {
      throw new Error("Cannot reorder tracks");
    }
    return { changed: "tracks" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const project = requireProject(context);
    if (!project.reorderTracks(this.toIndex, this.fromIndex)) {
      throw new Error("Cannot undo track reorder");
    }
    return { changed: "tracks" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/**
 * Replace one track’s instrument + mix + slot FX + bank links (pattern cells untouched).
 * One undo step for tracker “Paste instrument”.
 */
export class PasteInstrumentCommand implements AdamCommand {
  readonly id = "pasteInstrument";
  readonly label = "Paste instrument";

  private readonly from: InstrumentSlotPayload;
  private readonly to: InstrumentSlotPayload;

  constructor(
    readonly track: number,
    from: InstrumentSlotPayload,
    to: InstrumentSlotPayload,
  ) {
    this.from = cloneInstrumentSlotPayload(from);
    this.to = cloneInstrumentSlotPayload(to);
  }

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setInstrumentSlot(this.track, this.to);
    return { changed: "mixed" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setInstrumentSlot(this.track, this.from);
    return { changed: "mixed" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class SetPatternMuteCommand implements AdamCommand {
  readonly id = "setPatternMute";
  readonly label = "Mute track";

  constructor(
    readonly patternId: string,
    readonly track: number,
    readonly from: boolean,
    readonly to: boolean,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setPatternMute(this.patternId, this.track, this.to);
    return { changed: "muteSolo" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setPatternMute(this.patternId, this.track, this.from);
    return { changed: "muteSolo" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class SetSoloCommand implements AdamCommand {
  readonly id = "setSolo";
  readonly label = "Solo track";

  constructor(
    readonly track: number,
    readonly from: boolean,
    readonly to: boolean,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setSolo(this.track, this.to);
    return { changed: "muteSolo" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setSolo(this.track, this.from);
    return { changed: "muteSolo" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class SetTrackColorCommand implements AdamCommand {
  readonly id = "setTrackColor";
  readonly label = "Track color";

  constructor(
    readonly track: number,
    readonly from: number | null,
    readonly to: number | null,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setTrackColor(this.track, this.to);
    return { changed: "trackColor" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setTrackColor(this.track, this.from);
    return { changed: "trackColor" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetTrackColorCommand && command.track === this.track;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetTrackColorCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetTrackColorCommand(this.track, this.from, command.to);
  }
}
